"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * About 5005 — KUK "About the programme".
 *
 * Port of kuk-campus-portal `components/Landing/AboutCourse.tsx`: the block that
 * introduces the programme on every `/online-*-lp` page. A heading, a paragraph
 * of prose, and a set of key-content cards.
 *
 * TWO PRESENTATIONS, ONE SET OF ROWS. From 768px up the cards are a horizontal
 * rail with scroll buttons that appear only when there is something to scroll
 * to; below it they collapse into an accordion, one open at a time. That is v1's
 * behaviour exactly, and both read the same `metadata` rows.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * HEADING HIGHLIGHT — v1 splits the heading on spaces and paints any word that
 * lower-cases to "online" in its brand colour, so "About Online MBA" gets a
 * coloured middle word. A hardcoded word match is not something a CMS heading
 * can rely on (it silently does nothing for "About the MBA programme", and it
 * fires on a word the editor did not mean to colour), so this uses the app's
 * existing `{phrase}` convention instead — the editor writes `About {Online}
 * MBA` and the braced run takes `title_1_color`. Same output, and it works for
 * any phrase.
 *
 * DROPPED — v1's `justify-center` / `justify-start` pair on the rail. The two
 * classes are applied from overlapping conditions (`< 5` and `>= 3`), so for
 * three and four cards both land on the element and the later one silently
 * wins; the rail is `justify-start` at every count that matters. Reproduced as
 * the behaviour, not the contradiction: the rail starts at the left, and a set
 * narrower than the container is centred by `justify-center` only when it truly
 * cannot scroll.
 *
 * DROPPED — v1's fixed `w-[1120px]` on the rail's wrapper. It is wider than the
 * viewport below 1120px and has no `max-w`, so the reference overflows the page
 * horizontally on tablets; the wrapper here is `max-w`, which renders
 * identically at ≥1120px and stops the page scrolling sideways below it.
 */

/** v1: `min-w-[210px] max-w-[230px]` on each rail card. */
const CARD_MIN_WIDTH = 210;
const CARD_MAX_WIDTH = 230;
/** v1: `scrollBy({ left: ±200 })`. */
const SCROLL_STEP = 200;
/** v1: `Wrapper` is `md:w-[1120px]`. */
const DEFAULT_MAX_WIDTH = 1120;
/** v1 tolerates a sub-pixel gap before hiding the right arrow. */
const SCROLL_EPSILON = 1;

/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used
 * on 12 headings across the reference. Defined in ITS globals.css as a `::after`
 * with `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can
 * come from a CMS field (a pseudo-element cannot read an inline style), and so
 * the classes are ones Tailwind certainly emits — an arbitrary `after:` variant
 * would need verifying against the built CSS.
 *
 * `w-fit` on the heading is load-bearing: the rule is 33% of the HEADING, and a
 * full-width h2 would stretch it across a third of the page.
 */
const HEADING_UNDERLINE =
  "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

interface AboutCard {
  id?: string | number;
  title?: string;
  description?: string;
  priority?: number;
  status?: boolean;
}

interface About5005Data {
  status?: boolean | number | string;
  title?: string;
  title_text_color?: string;
  /** Colour applied to any `{braced}` run in the heading. */
  title_1_color?: string;
  description?: string;
  description_text_color?: string;
  background_color?: string;
  /** Card fill / bottom rule / text. */
  gradient_color_1?: string;
  highlight_border_color?: string;
  key_highlight_heading_color?: string;
  key_highlight_subtext_color?: string;
  /** Accordion row separator, below 768px. */
  key_highlight_divider_color?: string;
  /**
   * Leading glyph on each accordion row — an upload URL or an icon-map key.
   * Decorative: it sits next to the row's own label, so it is not announced and
   * takes no alt text.
   */
  cta_icon?: string;
  /** Rail scroll buttons. */
  read_more_color?: string;
  read_more_background_color?: string;
  metadata?: AboutCard[];
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Wrap any `{phrase}` in a coloured span. Mirrors ProgramBenefitsFeePlan. */
function highlight(text: string, color: string): string {
  if (!text) return "";
  return escapeHtml(text).replace(
    /\{([^}]+)\}/g,
    `<span style="color:${color}">$1</span>`,
  );
}

const About5005 = (props: { data?: About5005Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as About5005Data | undefined;

  const {
    title = "",
    title_text_color = "#101112",
    title_1_color = "#9F1C33",
    description = "",
    description_text_color = "#101112",
    background_color = "#FFFFFF",
    gradient_color_1 = "rgba(159, 28, 51, 0.10)",
    highlight_border_color = "#9F1C33",
    key_highlight_heading_color = "#101112",
    key_highlight_subtext_color = "#4B4B4B",
    key_highlight_divider_color = "#F5F5F5",
    cta_icon = "",
    read_more_color = "#101112",
    read_more_background_color = "#FFFFFF",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const cards = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.status !== false && (r?.title || r?.description))
      .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0));
  }, [sectionData?.metadata]);

  const railRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  /* Ids for the accordion's aria wiring — `useId` keeps them unique when the
     same widget is placed on a page twice. */
  const accordionId = useId();

  const syncArrows = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeft(scrollLeft > SCROLL_EPSILON);
    setShowRight(scrollLeft < scrollWidth - clientWidth - SCROLL_EPSILON);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    /* v1 only listens to `scroll`, so its right arrow is stuck on until the
       first scroll and never re-checks on resize — it shows an arrow on a rail
       that cannot scroll. Syncing on mount, on card-count change and on resize
       is the same rule, just applied when the answer can actually change. */
    syncArrows();
    el.addEventListener("scroll", syncArrows, { passive: true });
    window.addEventListener("resize", syncArrows);
    return () => {
      el.removeEventListener("scroll", syncArrows);
      window.removeEventListener("resize", syncArrows);
    };
  }, [syncArrows, cards.length]);

  const scroll = useCallback((direction: "left" | "right") => {
    railRef.current?.scrollBy({
      left: direction === "left" ? -SCROLL_STEP : SCROLL_STEP,
      behavior: "smooth",
    });
  }, []);

  if (!status) return null;

  const canScroll = showLeft || showRight;
  const arrowClassName =
    "absolute top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full shadow-md";
  const arrowStyle = {
    backgroundColor: read_more_background_color,
    color: read_more_color,
  };

  return (
    <section
      className="w-full"
      style={{ backgroundColor: background_color }}
    >
      <div
        className="mx-auto flex w-full flex-col items-center gap-5 px-5 py-10 tab:px-10 tab:pb-0 tab:pt-10"
        style={{ maxWidth: `${DEFAULT_MAX_WIDTH}px` }}
      >
        <div className="w-full tab:px-20">
          {title ? (
            <h2
              className="relative w-fit text-2xl font-bold tab:text-4xl"
              style={{ color: title_text_color }}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: highlight(title, title_1_color),
                }}
              />
              <span
                aria-hidden
                className={HEADING_UNDERLINE}
                style={{ backgroundColor: highlight_border_color }}
              />
            </h2>
          ) : null}

          {description ? (
            <p
              className="py-5 text-base font-light tracking-wide"
              style={{ color: description_text_color }}
            >
              {description}
            </p>
          ) : null}
        </div>

        {/* ── tab and up: horizontal card rail ───────────────────────────── */}
        {cards.length > 0 && (
          <div className="relative hidden w-full px-3 tab:block">
            <div
              ref={railRef}
              className={cn(
                "scrollbar-hide flex gap-2 overflow-x-auto pb-5",
                /* Only centre when the row genuinely fits — otherwise centring a
                   scrollable rail hides its first card behind the left edge. */
                canScroll ? "justify-start" : "justify-center",
              )}
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {cards.map((card, i) => (
                <div
                  key={card.id ?? i}
                  /* v1: `rounded-xl` on the rail card, with the accent rule as
                     a 4px bottom border inside that radius. */
                  className="flex h-auto flex-col items-center justify-between gap-7 rounded-xl border-b-4 p-5"
                  style={{
                    minWidth: `${CARD_MIN_WIDTH}px`,
                    maxWidth: `${CARD_MAX_WIDTH}px`,
                    backgroundColor: gradient_color_1,
                    borderBottomColor: highlight_border_color,
                  }}
                >
                  {card.title ? (
                    <p
                      className="text-base font-bold leading-5 tracking-wide"
                      style={{ color: key_highlight_heading_color }}
                    >
                      {card.title}
                    </p>
                  ) : null}
                  {card.description ? (
                    <p
                      className="mb-2 text-sm font-light"
                      style={{ color: key_highlight_subtext_color }}
                    >
                      {card.description}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>

            {showLeft && (
              <button
                type="button"
                aria-label="Scroll left"
                onClick={() => scroll("left")}
                className={cn(arrowClassName, "-left-14")}
                style={arrowStyle}
              >
                &#8592;
              </button>
            )}
            {showRight && (
              <button
                type="button"
                aria-label="Scroll right"
                onClick={() => scroll("right")}
                className={cn(arrowClassName, "-right-14")}
                style={arrowStyle}
              >
                &#8594;
              </button>
            )}
          </div>
        )}

        {/* ── below tab: accordion ───────────────────────────────────────── */}
        {cards.length > 0 && (
          <div className="w-full tab:hidden">
            {cards.map((card, i) => {
              const isOpen = openIndex === i;
              const panelId = `${accordionId}-panel-${i}`;
              const buttonId = `${accordionId}-button-${i}`;
              return (
                <div key={card.id ?? i}>
                  <h3>
                    <button
                      type="button"
                      id={buttonId}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenIndex(isOpen ? null : i)}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-base"
                      style={{ backgroundColor: key_highlight_divider_color }}
                    >
                      <span className="flex items-center gap-3">
                        {cta_icon ? (
                          <DynamicIcon
                            iconName={cta_icon}
                            width="18"
                            height="18"
                            color={highlight_border_color}
                          />
                        ) : null}
                        <span style={{ color: key_highlight_heading_color }}>
                          {card.title}
                        </span>
                      </span>
                      {/* A rotating chevron rather than v1's swapped +/- glyphs:
                          one element, so the open state cannot desync from the
                          icon. */}
                      <span
                        aria-hidden
                        className={cn(
                          "shrink-0 transition-transform duration-200",
                          isOpen && "rotate-180",
                        )}
                        style={{ color: key_highlight_heading_color }}
                      >
                        &#8964;
                      </span>
                    </button>
                  </h3>
                  {isOpen && card.description ? (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      className="px-5 py-3 text-sm font-light"
                      style={{ color: key_highlight_subtext_color }}
                    >
                      {card.description}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default About5005;
